import { TONE_COLORS } from "@eldrun/content";
import { Color, ShaderMaterial } from "three";
import { gameColor } from "../style/roles.ts";
import type { AtmosphereUniforms } from "../atmosphere.ts";
import { assemble } from "./chunks.ts";
import { DECAL_SLOTS } from "../decal-batch.ts";

const vertexShader = /* glsl */ `
in float aRadial;
in float aAngular;
// Lote (revisão da Fase 13, \`DecalBatch\`): o tipo (x) e a vida (y) de cada perigo na vaga dele.
in float aSlot;
uniform vec4 uSlot[${DECAL_SLOTS}];
out float vRadial;
out float vAngular;
out vec3 vWorldPos;
flat out vec2 vKindLife;

void main() {
  vRadial = aRadial;
  vAngular = aAngular;
  vKindLife = uSlot[int(aSlot + 0.5)].xy;
  vec4 world = modelMatrix * vec4(position, 1.0);
  vWorldPos = world.xyz;
  gl_Position = projectionMatrix * viewMatrix * world;
}
`;

const fragmentShader = /* glsl */ `
${assemble("fragment", ["atmo", "noise"])}
// Malha neon (F60): perigos de quem ataca, no laranja dos inimigos (pela luminância do desenho de cada um).
uniform vec3 uTone;
in float vRadial;
in float vAngular;
in vec3 vWorldPos;
flat in vec2 vKindLife;

void main() {
  float uKind = vKindLife.x;
  float uLife = vKindLife.y;
  // Entra em 0,2 s e some no último quarto da vida.
  float fade = smoothstep(0.0, 0.05, uLife) * (1.0 - smoothstep(0.75, 1.0, uLife));
  vec2 p = vWorldPos.xz;
  vec3 color;
  float alpha;
  if (uKind < 0.5) {
    // Rastro de fogo: chamas que tremulam e sobem, mais fortes no meio da faixa.
    float core = 1.0 - smoothstep(0.35, 1.0, abs(vAngular));
    float n = noise(p * 2.2 + vec2(0.0, -uTime * 3.0)) * 0.7 + noise(p * 5.0 - vec2(uTime * 2.0, 0.0)) * 0.3;
    float flame = smoothstep(0.35, 0.8, n * core + core * 0.25);
    color = mix(vec3(0.85, 0.18, 0.04), vec3(1.0, 0.82, 0.35), flame);
    alpha = (0.25 * core + 0.75 * flame) * fade;
  } else if (uKind > 2.5) {
    // Brasas do Guardião Vermelho: carvões que pulsam em laranja, mais densos no meio do disco.
    vec2 cell = floor(p * 3.0);
    float ember = step(0.55, hash12(cell)) * (0.6 + 0.4 * sin(uTime * 5.0 + hash12(cell + 3.0) * 6.28));
    float core = 1.0 - smoothstep(0.6, 1.0, vRadial);
    color = mix(vec3(0.25, 0.06, 0.02), vec3(1.0, 0.45, 0.1), ember);
    alpha = (0.35 + 0.55 * ember) * core * fade;
  } else if (uKind > 1.5) {
    // Mato fechado do Guardião Verde: tufos verdes que balançam.
    float n = noise(p * 3.0 + vec2(sin(uTime * 1.5), cos(uTime * 1.2)) * 0.3);
    float tuft = smoothstep(0.45, 0.75, n);
    color = mix(vec3(0.10, 0.28, 0.08), vec3(0.35, 0.62, 0.18), tuft);
    alpha = (0.5 + 0.4 * tuft) * (1.0 - smoothstep(0.9, 1.0, vRadial)) * fade;
  } else {
    // Poça do caldeirão: verde-escuro com bolhas que estouram e borda viscosa.
    float edge = smoothstep(0.85, 1.0, vRadial);
    vec2 cell = floor(p * 2.5);
    vec2 local = fract(p * 2.5) - 0.5;
    float t = fract(uTime * 0.6 + hash12(cell));
    float bubble = (1.0 - smoothstep(0.08, 0.16 * t + 0.08, length(local))) * step(0.6, hash12(cell + 7.0)) * (1.0 - t);
    color = mix(vec3(0.12, 0.26, 0.10), vec3(0.45, 0.62, 0.22), bubble + edge * 0.5);
    alpha = (0.62 + 0.3 * bubble) * (1.0 - smoothstep(0.92, 1.0, vRadial)) * fade;
  }
  color = uTone * (0.35 + 1.6 * dot(color, vec3(0.2126, 0.7152, 0.0722)));
  gl_FragColor = vec4(color, alpha);
  #include <colorspace_fragment>
}
`;

export const HAZARD_KIND = { fogo: 0, poca: 1, mato: 2, brasa: 3 } as const;

/** Material das áreas que machucam (rastro de fogo, poça da Cuca, mato e brasas dos Guardiões), em lote. */
export function createHazardMaterial(atmosphere: AtmosphereUniforms): ShaderMaterial {
  return new ShaderMaterial({
    name: "perigo",
    uniforms: { ...atmosphere, uTone: { value: new Color(gameColor(TONE_COLORS.inimigo)) } },
    vertexShader,
    fragmentShader,
    transparent: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -2,
  });
}
